import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { collection, addDoc, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAppData } from '../context/AppDataContext';
import '../styles/ClientList.css'; // Reusing styles
import '../styles/FacebookEventsPage.css';
import SearchBar from '../components/SearchBar';

export default function FacebookEventsPage({ userRole }) {
  const { clients: cachedClients, facebookEvents: cachedEvents, loaded, refresh } = useAppData();

  // ━━━ STATE ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const [events, setEvents] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [specificField, setSpecificField] = useState('startDate');
  const [specificDate, setSpecificDate] = useState('');
  const [newEvent, setNewEvent] = useState({
    jobName: 'November Blast #',
    clientId: '',
    startDate: '',
    endDate: '',
    liveDate: '',
    voucherUrl: '',
    note: ''
  });

  // ━━━ EDIT STATE ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const [editingEventId, setEditingEventId] = useState(null);
  const [editEvent, setEditEvent] = useState({
    jobName: '',
    clientId: '',
    startDate: '',
    endDate: '',
    liveDate: '',
    voucherUrl: '',
    note: ''
  });

  // ━━━ MEMOIZE CLIENT LOOKUP ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const clientMap = useMemo(() => {
    const m = {};
    clients.forEach(c => { m[c.id] = c; });
    return m;
  }, [clients]);

  // ━━━ DATE PARSING ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const parseDate = useCallback(value => {
    if (!value) return null;
    
    // Handle Firestore timestamp
    if (value.seconds !== undefined && typeof value.toDate === 'function') {
      return value.toDate();
    }
    
    // Handle string dates
    if (typeof value === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [y, m, d] = value.split('-').map(Number);
        return new Date(y, m - 1, d);
      }
    }
    
    // Handle regular date objects
    const dt = new Date(value);
    return isNaN(dt.getTime()) ? null : dt;
  }, []);

  // ━━━ HELPER: SAME-DAY CHECK ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const isSameDay = useCallback((d1, d2) => {
    if (!d1 || !d2) return false;
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate();
  }, []);

  // ━━━ HELPER: GET WEEK OF MONTH ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const getWeekOfMonth = useCallback((date) => {
    if (!date) return null;
    const dayOfMonth = date.getDate();
    const weekNumber = Math.ceil(dayOfMonth / 7);
    return weekNumber;
  }, []);

  // ━━━ HELPER: GET WEEK RANGE ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const getWeekRange = useCallback((year, month, weekNumber) => {
    const startDay = (weekNumber - 1) * 7 + 1;
    const endDay = Math.min(weekNumber * 7, new Date(year, month + 1, 0).getDate());
    
    const startDate = new Date(year, month, startDay);
    const endDate = new Date(year, month, endDay);
    
    const options = { month: 'short', day: 'numeric' };
    return `${startDate.toLocaleDateString('en-US', options)} - ${endDate.toLocaleDateString('en-US', options)}`;
  }, []);

  // ━━━ HELPER: LIVE EVENT? ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const isLive = useCallback(event => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const rawStart = event.liveDate || event.startDate || event.endDate;
    const rawEnd = event.endDate || event.startDate || event.liveDate;
    
    const start = parseDate(rawStart);
    const end = parseDate(rawEnd);
    
    if (!start || !end) return false;
    
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    
    return today >= start && today <= end;
  }, [parseDate]);

  // ━━━ LOAD FROM CACHE ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  useEffect(() => {
    if (!loaded) return;

    const evts = cachedEvents.map(e => ({ ...e, active: e.active !== false }));

    // facebook-only role filter
    let filtered = evts;
    if (userRole === 'facebook') {
      filtered = filtered.filter(isLive);
    }

    // sort events by date
    filtered.sort((a, b) => {
      const aD = parseDate(a.endDate || a.startDate || a.liveDate) || new Date(0);
      const bD = parseDate(b.endDate || b.startDate || b.liveDate) || new Date(0);
      return aD - bD;
    });

    // sort clients by name
    const clnts = [...cachedClients].sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    setClients(clnts);
    setEvents(filtered);
    setLoading(false);
  }, [loaded, cachedEvents, cachedClients, userRole, isLive, parseDate]);

  // ━━━ HANDLERS ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const handleNewEventChange = useCallback((field, value) => {
    setNewEvent(prev => ({ ...prev, [field]: value }));
  }, []);

  // ━━━ EDIT HANDLERS ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const startEditing = useCallback((event) => {
    setEditingEventId(event.id);
    setEditEvent({
      jobName: event.jobName || '',
      clientId: event.clientId || '',
      startDate: event.startDate || '',
      endDate: event.endDate || '',
      liveDate: event.liveDate || '',
      voucherUrl: event.voucherUrl || '',
      note: event.note || ''
    });
  }, []);

  const handleEditEventChange = useCallback((field, value) => {
    setEditEvent(prev => ({ ...prev, [field]: value }));
  }, []);

  const saveEditEvent = async () => {
    if (!editEvent.jobName.trim()) {
      alert('Enter a Job Name');
      return;
    }
    if (!editEvent.clientId) {
      alert('Select a Client');
      return;
    }
    if (!editEvent.startDate) {
      alert('Pick a Start date');
      return;
    }
    if (!editEvent.endDate) {
      alert('Pick an End date');
      return;
    }
    
    try {
      await updateDoc(doc(db, 'facebookEvents', editingEventId), editEvent);
      refresh(['facebookEvents']);
      setEditingEventId(null);
      setEditEvent({
        jobName: '',
        clientId: '',
        startDate: '',
        endDate: '',
        liveDate: '',
        voucherUrl: '',
        note: ''
      });
      alert('Event updated successfully!');
    } catch (err) {
      console.error('Update failed:', err);
      alert('Error updating event');
    }
  };

  const cancelEdit = useCallback(() => {
    setEditingEventId(null);
    setEditEvent({
      jobName: '',
      clientId: '',
      startDate: '',
      endDate: '',
      liveDate: '',
      voucherUrl: '',
      note: ''
    });
  }, []);

  const saveNewEvent = async () => {
    if (!newEvent.jobName.trim()) {
      alert('Enter a Job Name');
      return;
    }
    if (!newEvent.clientId) {
      alert('Select a Client');
      return;
    }
    if (!newEvent.startDate) {
      alert('Pick a Start date');
      return;
    }
    if (!newEvent.endDate) {
      alert('Pick an End date');
      return;
    }
    
    try {
      await addDoc(collection(db, 'facebookEvents'), { 
        ...newEvent, 
        active: true,
        createdAt: new Date()
      });
      refresh(['facebookEvents']);
      setNewEvent({ 
        jobName: 'November Blast #', 
        clientId: '', 
        startDate: '', 
        endDate: '', 
        liveDate: '', 
        voucherUrl: '',
        note: ''
      });
      alert('Event saved successfully!');
    } catch (err) {
      console.error('Save failed:', err);
      alert('Error saving event');
    }
  };

  const deleteEvent = async id => {
    if (!window.confirm('Archive this event?')) return;
    
    try {
      await updateDoc(doc(db, 'facebookEvents', id), { active: false });
      refresh(['facebookEvents']);
    } catch (err) {
      console.error('Archive failed:', err);
      alert('Error archiving event');
    }
  };

  // ━━━ INLINE FILTERS ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const bySearch = useCallback(ev => {
    const term = search.toLowerCase();
    const job = (ev.jobName || '').toLowerCase();
    const name = (clientMap[ev.clientId]?.name || '').toLowerCase();
    return job.includes(term) || name.includes(term);
  }, [search, clientMap]);

  const byDate = useCallback(ev => {
    const today = new Date(); 
    today.setHours(0, 0, 0, 0);
    
    switch (filterType) {
      case 'current': 
        return isLive(ev);
      case 'start':   
        return isSameDay(parseDate(ev.startDate), today);
      case 'end':     
        return isSameDay(parseDate(ev.endDate), today);
      case 'live':    
        return isSameDay(parseDate(ev.liveDate), today);
      case 'specific': {
        if (!specificDate) return false;
        const [y, m, d] = specificDate.split('-').map(Number);
        const pick = new Date(y, m - 1, d); 
        pick.setHours(0, 0, 0, 0);
        return isSameDay(parseDate(ev[specificField]), pick);
      }
      default:        
        return true;
    }
  }, [filterType, specificDate, specificField, parseDate, isLive, isSameDay]);

  const eventsToDisplay = useMemo(
    () => events.filter(bySearch).filter(byDate),
    [events, bySearch, byDate]
  );

  // ━━━ GROUPING ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const groupEvents = useCallback(list => {
    return list.reduce((acc, ev) => {
      const raw = ev.endDate || ev.startDate || ev.liveDate;
      const dt = parseDate(raw);
      const monthKey = dt ? dt.toLocaleString('default', { 
        month: 'long', 
        year: 'numeric' 
      }) : 'No Date';
      
      if (!acc[monthKey]) acc[monthKey] = {};
      
      // Get week number for this event
      const weekNumber = dt ? getWeekOfMonth(dt) : 0;
      const weekKey = `Week${weekNumber}`;
      
      if (!acc[monthKey][weekKey]) {
        acc[monthKey][weekKey] = {
          weekNumber,
          year: dt ? dt.getFullYear() : 0,
          month: dt ? dt.getMonth() : 0,
          events: []
        };
      }
      
      acc[monthKey][weekKey].events.push(ev);
      return acc;
    }, {});
  }, [parseDate, getWeekOfMonth]);

  // ━━━ VOUCHER HELPERS ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Blob URL + attached link: data-URL downloads on a detached <a> silently fail on iPad/Safari.
  const downloadCanvas = (canvas, filename) => {
    try {
      canvas.toBlob(blob => {
        if (!blob) {
          alert('Could not generate voucher image');
          return;
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }, 'image/png');
    } catch (err) {
      console.error('Voucher export failed:', err);
      alert('Voucher image was blocked by the browser (CORS). Notify Admin.');
    }
  };

  // Cache-bust so Safari can't reuse a non-CORS cached copy of the template
  const corsSafe = src => src + (src.includes('?') ? '&' : '?') + 'cb=' + Date.now();

  const handleGenerateVoucher = async (event, client) => {
    try {
      let cleanDate = 'Invalid Date';
      let year = '';
      
      // Better date parsing for voucher
      const endDateValue = event.endDate;
      let rawDate;
      
      if (endDateValue?.seconds) {
        rawDate = new Date(endDateValue.seconds * 1000);
      } else if (typeof endDateValue === 'string') {
        rawDate = new Date(`${endDateValue}T00:00:00`);
      } else {
        rawDate = new Date(endDateValue);
      }

      if (!isNaN(rawDate.getTime())) {
        const day = rawDate.getDate();
        year = rawDate.getFullYear();
        const month = rawDate.toLocaleString('en-US', { month: 'long' });

        const getOrdinal = (n) => {
          if (n > 3 && n < 21) return 'th';
          switch (n % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
          }
        };

        cleanDate = `${month} ${day}${getOrdinal(day)}, ${year}`;
      }

      const cleanedName = (client?.name || '')
        .replace(/\n|\r/g, '')
        .replace(/\u00A0/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      const scale = 2;
      const canvas = document.createElement('canvas');
      canvas.width = 612 * scale;
      canvas.height = 792 * scale;
      const ctx = canvas.getContext('2d');
      ctx.scale(scale, scale);

      const today = new Date();
      const formattedDate = `${(today.getMonth() + 1).toString().padStart(2, '0')}${today.getDate().toString().padStart(2, '0')}${today.getFullYear()}`;

      const background = new Image();
      background.crossOrigin = "anonymous";
      
      background.onload = () => {
        try {
          ctx.drawImage(background, 0, 0, 612, 792);
          
          // Add expiration date
          const numericDate = `${(rawDate.getMonth() + 1).toString().padStart(2, '0')}/${rawDate.getDate().toString().padStart(2, '0')}/${year}`;
          ctx.fillStyle = 'black';
          ctx.textAlign = 'right';
          ctx.font = 'bold 18px Arial';
          ctx.fillText(`EXPIRES: ${numericDate}`, 600, 280);
          
          ctx.fillStyle = 'black';
          ctx.font = 'bold 22px "Capellina Caps", serif';
          ctx.textAlign = 'center';
          ctx.fillText(`Expires: ${cleanDate}`, 306, 141);

          // Same filenames as before: dealership name if it has a logo, else job name
          const baseName = client?.blackLogo ? cleanedName : (event.jobName || 'voucher');
          downloadCanvas(canvas, `${baseName}_${formattedDate}.png`);
        } catch (err) {
          console.error('Error drawing voucher:', err);
          alert('Error generating voucher');
        }
      };
      
      background.onerror = () => {
        alert('Notify Admin this client has no voucher');
      };
      
      background.src = corsSafe(client?.voucherTemplate || `${window.location.origin}/voucher.jpg`);
      
    } catch (error) {
      console.error('Error generating voucher:', error);
      alert('Error generating voucher');
    }
  };

  const handleGenerateVoucher2 = async (event, client) => {
    if (!event.endDate) {
      alert('Event must have an end date to generate voucher');
      return;
    }
    
    try {
      const canvas = document.createElement('canvas');
      const scale = 2;
      canvas.width = 612 * scale;
      canvas.height = 792 * scale;
      const ctx = canvas.getContext('2d');
      ctx.scale(scale, scale);
      
      const background = new Image();
      background.crossOrigin = 'anonymous';
      
      background.onload = () => {
        ctx.drawImage(background, 0, 0, 612, 792);
        
        // Better date parsing (same as original voucher)
        const endDateValue = event.endDate;
        let rawDate;
        
        if (endDateValue?.seconds) {
          rawDate = new Date(endDateValue.seconds * 1000);
        } else if (typeof endDateValue === 'string') {
          rawDate = new Date(`${endDateValue}T00:00:00`);
        } else {
          rawDate = new Date(endDateValue);
        }
        
        // Format the date as MM/DD/YYYY
        const month = (rawDate.getMonth() + 1).toString().padStart(2, '0');
        const day = rawDate.getDate().toString().padStart(2, '0');
        const year = rawDate.getFullYear();
        const numericDate = `${month}/${day}/${year}`;
        
        // Format the filename
        const cleanedName = (event.jobName || 'voucher')
          .replace(/[^a-zA-Z0-9]/g, '_')
          .replace(/_+/g, '_')
          .replace(/^_|_$/g, '');
        
        const today = new Date();
        const formattedDate = `${(today.getMonth() + 1).toString().padStart(2, '0')}${today.getDate().toString().padStart(2, '0')}${today.getFullYear()}`;
        
        // Draw expiration date at a LOWER position (different from regular voucher)
        ctx.fillStyle = 'black';
        ctx.font = 'bold 18px Arial';
        ctx.textAlign = 'right';
        ctx.fillText(`EXPIRES: ${numericDate}`, 600, 315);

        // Same filenames as before: cleaned job name if client has a logo, else raw job name
        const baseName = client?.blackLogo ? cleanedName : (event.jobName || 'voucher');
        downloadCanvas(canvas, `${baseName}_voucher2_${formattedDate}.png`);
      };
      
      background.onerror = () => {
        alert('Notify Admin this client has no voucher');
      };
      
      background.src = corsSafe(client?.voucherTemplate2 || `${window.location.origin}/voucher2.jpg`);
      
    } catch (error) {
      console.error('Error generating voucher 2:', error);
      alert('Error generating voucher 2');
    }
  };

  // ━━━ MONTH VIEW ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const groupedEvents = useMemo(
    () => groupEvents(eventsToDisplay),
    [groupEvents, eventsToDisplay]
  );

  // Month keys sorted newest-first (same year/month sort as before)
  const sortedMonthKeys = useMemo(() => {
    return Object.entries(groupedEvents)
      .sort((a, b) => {
        const weeksA = Object.values(a[1]);
        const weeksB = Object.values(b[1]);

        if (weeksA.length === 0 || weeksB.length === 0) return 0;

        const yearA = weeksA[0].year;
        const monthA = weeksA[0].month;
        const yearB = weeksB[0].year;
        const monthB = weeksB[0].month;

        if (yearB !== yearA) return yearB - yearA;
        return monthB - monthA;
      })
      .map(([month]) => month);
  }, [groupedEvents]);

  // Active-event count per month (for the navigator badges + "Total Events" line)
  const monthActiveCounts = useMemo(() => {
    const counts = {};
    Object.entries(groupedEvents).forEach(([month, weeks]) => {
      counts[month] = Object.values(weeks).reduce(
        (sum, weekData) => sum + weekData.events.filter(ev => ev.active).length,
        0
      );
    });
    return counts;
  }, [groupedEvents]);

  // Search or a date filter overrides the single-month view and shows all matches
  const isFiltering = search.trim() !== '' || filterType !== 'all';

  // Default selection: current month if it has events, otherwise newest month
  useEffect(() => {
    if (isFiltering) return; // don't move the selection while search/filters narrow the list
    if (sortedMonthKeys.length === 0) return;
    if (selectedMonth && sortedMonthKeys.includes(selectedMonth)) return;

    const currentKey = new Date().toLocaleString('default', {
      month: 'long',
      year: 'numeric'
    });
    setSelectedMonth(
      sortedMonthKeys.includes(currentKey) ? currentKey : sortedMonthKeys[0]
    );
  }, [sortedMonthKeys, selectedMonth, isFiltering]);

  // Keys are sorted newest-first, so "previous month" = index + 1
  const selectedIndex = sortedMonthKeys.indexOf(selectedMonth);
  const goToOlderMonth = () => {
    if (selectedIndex < sortedMonthKeys.length - 1) {
      setSelectedMonth(sortedMonthKeys[selectedIndex + 1]);
    }
  };
  const goToNewerMonth = () => {
    if (selectedIndex > 0) {
      setSelectedMonth(sortedMonthKeys[selectedIndex - 1]);
    }
  };

  const monthsToRender = isFiltering
    ? sortedMonthKeys
    : (selectedMonth && groupedEvents[selectedMonth] ? [selectedMonth] : []);

  return (
    <div className="client-page">
      <h1 className="gradient-text">Facebook Events</h1>

      {/* Search Bar */}
      {(userRole === 'admin' || userRole === 'facebook' || userRole === 'facebookAdmin') && (
        <SearchBar
          placeholder="🔍 Search events..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ padding: '8px', marginBottom: '20px', width: '100%', maxWidth: '800px' }}
        />
      )}

      {/* Filters */}
      {userRole !== 'facebook' && (
        <div style={{ margin: '20px 0' }}>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '8px' }}>
            <button
              onClick={() => setFilterType('all')}
              style={{
                padding: '8px 16px',
                backgroundColor: filterType === 'all' ? '#003cff' : '#f0f0f0',
                color: filterType === 'all' ? 'white' : '#333',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: filterType === 'all' ? 'bold' : 'normal',
                fontSize: '14px'
              }}
            >
              All Events
            </button>
            <button
              onClick={() => setFilterType('current')}
              style={{
                padding: '8px 16px',
                backgroundColor: filterType === 'current' ? '#003cff' : '#f0f0f0',
                color: filterType === 'current' ? 'white' : '#333',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: filterType === 'current' ? 'bold' : 'normal',
                fontSize: '14px'
              }}
            >
              Current Events
            </button>
            <button
              onClick={() => setFilterType('start')}
              style={{
                padding: '8px 16px',
                backgroundColor: filterType === 'start' ? '#003cff' : '#f0f0f0',
                color: filterType === 'start' ? 'white' : '#333',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: filterType === 'start' ? 'bold' : 'normal',
                fontSize: '14px'
              }}
            >
              Starting Today
            </button>
            <button
              onClick={() => setFilterType('end')}
              style={{
                padding: '8px 16px',
                backgroundColor: filterType === 'end' ? '#003cff' : '#f0f0f0',
                color: filterType === 'end' ? 'white' : '#333',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: filterType === 'end' ? 'bold' : 'normal',
                fontSize: '14px'
              }}
            >
              Ending Today
            </button>
            <button
              onClick={() => setFilterType('live')}
              style={{
                padding: '8px 16px',
                backgroundColor: filterType === 'live' ? '#003cff' : '#f0f0f0',
                color: filterType === 'live' ? 'white' : '#333',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: filterType === 'live' ? 'bold' : 'normal',
                fontSize: '14px'
              }}
            >
              Going Live Today
            </button>
            <button
              onClick={() => setFilterType('specific')}
              style={{
                padding: '8px 16px',
                backgroundColor: filterType === 'specific' ? '#003cff' : '#f0f0f0',
                color: filterType === 'specific' ? 'white' : '#333',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: filterType === 'specific' ? 'bold' : 'normal',
                fontSize: '14px'
              }}
            >
              Specific Date...
            </button>
          </div>
          
          {filterType === 'specific' && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '12px' }}>
              <label>
                Date:
                <input 
                  type="date" 
                  value={specificDate} 
                  onChange={e => setSpecificDate(e.target.value)} 
                />
              </label>
              <label>
                Match:
                <select 
                  value={specificField} 
                  onChange={e => setSpecificField(e.target.value)}
                >
                  <option value="startDate">Starts On</option>
                  <option value="endDate">Ends On</option>
                  <option value="liveDate">Goes Live On</option>
                </select>
              </label>
            </div>
          )}
        </div>
      )}

      {/* Add New Event Form */}
      {userRole !== 'facebook' && (
        <>
          <div style={{
            backgroundColor: '#f8f9fa',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            padding: '20px',
            marginTop: '30px',
            marginBottom: '30px',
            maxWidth: '1000px'
          }}>
            <h2 style={{ 
              marginTop: '0', 
              marginBottom: '16px',
              color: '#333',
              fontSize: '18px'
            }}>
              Add New Event
            </h2>

            <div style={{
              display: 'flex',
              gap: '12px',
              alignItems: 'flex-end',
              flexWrap: 'wrap'
            }}>
              <div style={{ flex: '2 1 180px', display: 'flex', flexDirection: 'column' }}>
                <label style={{ 
                  fontWeight: 'bold', 
                  marginBottom: '4px',
                  fontSize: '13px',
                  color: '#555'
                }}>
                  Job Name
                </label>
                <input
                  placeholder="Enter job name"
                  value={newEvent.jobName}
                  onChange={e => handleNewEventChange('jobName', e.target.value)}
                  style={{
                    padding: '8px 10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: 'white'
                  }}
                />
              </div>

              <div style={{ flex: '2 1 180px', display: 'flex', flexDirection: 'column' }}>
                <label style={{ 
                  fontWeight: 'bold', 
                  marginBottom: '4px',
                  fontSize: '13px',
                  color: '#555'
                }}>
                  Client
                </label>
                <select
                  value={newEvent.clientId}
                  onChange={e => handleNewEventChange('clientId', e.target.value)}
                  style={{
                    padding: '8px 10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">Select Client</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div style={{ flex: '1 1 130px', display: 'flex', flexDirection: 'column' }}>
                <label style={{ 
                  fontWeight: 'bold', 
                  marginBottom: '4px',
                  fontSize: '13px',
                  color: '#555'
                }}>
                  Start Date
                </label>
                <input
                  type="date"
                  value={newEvent.startDate}
                  onChange={e => handleNewEventChange('startDate', e.target.value)}
                  style={{
                    padding: '8px 10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: 'white'
                  }}
                />
              </div>

              <div style={{ flex: '1 1 130px', display: 'flex', flexDirection: 'column' }}>
                <label style={{ 
                  fontWeight: 'bold', 
                  marginBottom: '4px',
                  fontSize: '13px',
                  color: '#555'
                }}>
                  End Date
                </label>
                <input
                  type="date"
                  value={newEvent.endDate}
                  onChange={e => handleNewEventChange('endDate', e.target.value)}
                  style={{
                    padding: '8px 10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: 'white'
                  }}
                />
              </div>

              <div style={{ flex: '1 1 130px', display: 'flex', flexDirection: 'column' }}>
                <label style={{ 
                  fontWeight: 'bold', 
                  marginBottom: '4px',
                  fontSize: '13px',
                  color: '#555'
                }}>
                  Live Date
                </label>
                <input
                  type="date"
                  value={newEvent.liveDate}
                  onChange={e => handleNewEventChange('liveDate', e.target.value)}
                  style={{
                    padding: '8px 10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: 'white'
                  }}
                />
              </div>

              <div style={{ flex: '2 1 200px', display: 'flex', flexDirection: 'column' }}>
                <label style={{ 
                  fontWeight: 'bold', 
                  marginBottom: '4px',
                  fontSize: '13px',
                  color: '#555'
                }}>
                  Note (Optional)
                </label>
                <input
                  type="text"
                  value={newEvent.note}
                  onChange={e => handleNewEventChange('note', e.target.value)}
                  placeholder="Add a note..."
                  style={{
                    padding: '8px 10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: 'white',
                    height: '38px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <button
                onClick={saveNewEvent}
                style={{
                  backgroundColor: '#006effff',
                  color: 'white',
                  border: 'none',
                  padding: '8px 20px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  height: '38px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s'
                }}
                onMouseOver={e => e.target.style.backgroundColor = '#006effff'}
                onMouseOut={e => e.target.style.backgroundColor = '#006effff'}
              >
                💾 Save Event
              </button>
            </div>
          </div>
        </>
      )}

      {/* Month Navigator (hidden while search/date filters are active) */}
      {!loading && !isFiltering && sortedMonthKeys.length > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          width: '100%',
          maxWidth: '1000px',
          margin: '10px 0 20px'
        }}>
          <button
            onClick={goToOlderMonth}
            disabled={selectedIndex >= sortedMonthKeys.length - 1}
            style={{
              padding: '6px 14px',
              fontSize: '18px',
              cursor: selectedIndex >= sortedMonthKeys.length - 1 ? 'default' : 'pointer',
              opacity: selectedIndex >= sortedMonthKeys.length - 1 ? 0.4 : 1,
              border: '1px solid #003cff',
              borderRadius: '8px',
              background: 'white',
              color: '#003cff',
              fontWeight: 'bold'
            }}
            aria-label="Older month"
          >
            ‹
          </button>

          <select
            value={selectedMonth || ''}
            onChange={e => setSelectedMonth(e.target.value)}
            style={{
              padding: '8px 12px',
              fontSize: '16px',
              fontWeight: 'bold',
              border: '1px solid #003cff',
              borderRadius: '8px',
              color: '#003cff',
              background: 'white',
              cursor: 'pointer',
              minWidth: '220px',
              textAlign: 'center'
            }}
          >
            {sortedMonthKeys.map(m => (
              <option key={m} value={m}>
                {m} ({monthActiveCounts[m] || 0})
              </option>
            ))}
          </select>

          <button
            onClick={goToNewerMonth}
            disabled={selectedIndex <= 0}
            style={{
              padding: '6px 14px',
              fontSize: '18px',
              cursor: selectedIndex <= 0 ? 'default' : 'pointer',
              opacity: selectedIndex <= 0 ? 0.4 : 1,
              border: '1px solid #003cff',
              borderRadius: '8px',
              background: 'white',
              color: '#003cff',
              fontWeight: 'bold'
            }}
            aria-label="Newer month"
          >
            ›
          </button>
        </div>
      )}

      {/* Events Display */}
      {loading ? (
        <p>Loading...</p>
      ) : (
        monthsToRender.map(month => {
          const weeks = groupedEvents[month];
          const totalActiveEvents = monthActiveCounts[month] || 0;

          return (
            <div key={month} style={{ width: '100%', maxWidth: '1000px', marginBottom: '30px' }}>
              <h3 className="gradient-text-smaller">
                {month}
              </h3>
              <p style={{ fontWeight: 'bold', marginTop: '10px', textAlign: 'left' }}>
                Total Events: {totalActiveEvents}
              </p>
              
              {Object.keys(weeks).sort((a, b) => {
                const weekA = parseInt(a.replace('Week', ''));
                const weekB = parseInt(b.replace('Week', ''));
                return weekA - weekB;
              }).map(weekKey => {
                const weekData = weeks[weekKey];
                const weekRange = getWeekRange(weekData.year, weekData.month, weekData.weekNumber);
                
                return (
                  <div key={weekKey}>
                    {/* Week Separator */}
                    <div style={{
                      backgroundColor: '#f0f0f0',
                      padding: '8px 12px',
                      marginTop: '15px',
                      marginBottom: '10px',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      color: '#555',
                      borderLeft: '4px solid #003cff',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span>Week {weekData.weekNumber} ({weekRange})</span>
                      <span style={{ fontSize: '13px', fontWeight: 'normal' }}>
                        {weekData.events.filter(ev => ev.active).length} event{weekData.events.filter(ev => ev.active).length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    
                    {/* Events in this week */}
                    {weekData.events.map(ev => {
                      const client = clientMap[ev.clientId];
                      const disabled = !ev.active;
                      const isEditing = editingEventId === ev.id;
              
              return (
                <div 
                  key={ev.id} 
                  className={`client-item ${disabled ? 'disabled-event' : ''}`} 
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    flexWrap: 'wrap', 
                    padding: '10px 2px',
                    marginBottom: '10px',
                    contentVisibility: 'auto',
                    containIntrinsicSize: 'auto 150px'
                  }}
                >
                  {client?.blackLogo && (
                    <div style={{ flex: '0 0 auto', marginRight: '16px' }}>
                      <img 
                        src={client.blackLogo} 
                        alt={`${client.name} logo`} 
                        loading="lazy"
                        decoding="async"
                        style={{ width: '120px', height: 'auto' }} 
                      />
                    </div>
                  )}
                  
                  <div style={{ flex: 3, minWidth: '200px' }}>
                    {isEditing ? (
                      // Edit mode - show inputs
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div>
                          <label style={{ fontWeight: 'bold', marginRight: '8px' }}>Job:</label>
                          <input
                            value={editEvent.jobName}
                            onChange={e => handleEditEventChange('jobName', e.target.value)}
                            style={{ padding: '4px', width: '200px' }}
                          />
                        </div>
                        <div>
                          <label style={{ fontWeight: 'bold', marginRight: '8px' }}>Client:</label>
                          <select
                            value={editEvent.clientId}
                            onChange={e => handleEditEventChange('clientId', e.target.value)}
                            style={{ padding: '4px', width: '200px' }}
                          >
                            <option value="">Select Client</option>
                            {clients.map(c => (
                              <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    ) : (
                      // View mode - show text
                      <div>
                        <div><strong>Job:</strong> {ev.jobName}</div>
                        <div><strong>Dealership:</strong> {client?.name || 'Unknown'}</div>
                        {client?.disclaimerLink && (
                          <div>
                            <strong>Disclaimer:</strong>{' '}
                            <a
                              href={
                                client.disclaimerLink.startsWith('http')
                                  ? client.disclaimerLink
                                  : `https://${client.disclaimerLink}`
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: '#003cff',
                                textDecoration: 'underline'
                              }}
                            >
                              {client.disclaimerLink}
                            </a>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div style={{ flex: 2, minWidth: '150px' }}>
                    {isEditing ? (
                      // Edit mode - show date inputs
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div>
                          <label style={{ fontWeight: 'bold', marginRight: '8px' }}>Start:</label>
                          <input
                            type="date"
                            value={editEvent.startDate}
                            onChange={e => handleEditEventChange('startDate', e.target.value)}
                            style={{ padding: '4px' }}
                          />
                        </div>
                        <div>
                          <label style={{ fontWeight: 'bold', marginRight: '8px' }}>End:</label>
                          <input
                            type="date"
                            value={editEvent.endDate}
                            onChange={e => handleEditEventChange('endDate', e.target.value)}
                            style={{ padding: '4px' }}
                          />
                        </div>
                        <div>
                          <label style={{ fontWeight: 'bold', marginRight: '8px' }}>Live:</label>
                          <input
                            type="date"
                            value={editEvent.liveDate}
                            onChange={e => handleEditEventChange('liveDate', e.target.value)}
                            style={{ padding: '4px' }}
                          />
                        </div>
                        <div>
                          <label style={{ fontWeight: 'bold', marginRight: '8px' }}>Note:</label>
                          <input
                            type="text"
                            value={editEvent.note}
                            onChange={e => handleEditEventChange('note', e.target.value)}
                            placeholder="Add a note..."
                            style={{ padding: '4px', width: '200px' }}
                          />
                        </div>
                      </div>
                    ) : (
                      // View mode - show text
                      <div>
                        <div><strong>Start:</strong> {ev.startDate || 'N/A'}</div>
                        <div><strong>End:</strong> {ev.endDate || 'N/A'}</div>
                        <div><strong>GOES LIVE:</strong> {ev.liveDate || 'N/A'}</div>
                        {ev.note && <div><strong>Note:</strong> {ev.note}</div>}
                      </div>
                    )}
                  </div>
                  
                  <div className="client-actions" style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'row', gap: '8px', alignItems: 'center' }}>
                    {isEditing ? (
                      // Edit mode buttons
                      <>
                        <button 
                          onClick={saveEditEvent}
                          style={{
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            minWidth: '70px'
                          }}
                        >
                          💾 Save
                        </button>
                        <button 
                          onClick={cancelEdit}
                          style={{
                            backgroundColor: '#6c757d',
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            minWidth: '70px'
                          }}
                        >
                          ❌ Cancel
                        </button>
                      </>
                    ) : (
                      // View mode buttons
                      <>
                        {userRole !== 'facebook' && (
                          <button 
                            onClick={() => startEditing(ev)}
                            disabled={disabled}
                            style={{
                              backgroundColor: disabled ? '#ccc' : '#007bff',
                              color: 'white',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '4px',
                              cursor: disabled ? 'not-allowed' : 'pointer',
                              fontSize: '14px',
                              minWidth: '140px'
                            }}
                          >
                            ✏️ Edit Event
                          </button>
                        )}
                        
                        <button 
                          onClick={() => handleGenerateVoucher(ev, client)} 
                          disabled={disabled}
                          style={{ minWidth: '140px' }}
                        >
                          ⬇️ Download Event Voucher
                        </button>
                        
                        <button 
                          onClick={() => handleGenerateVoucher2(ev, client)} 
                          disabled={disabled}
                          style={{ minWidth: '140px' }}
                        >
                          ⬇️ Download $500 Off Voucher
                        </button>
                        
                        {userRole !== 'facebook' && (
                          <button 
                            onClick={() => deleteEvent(ev.id)} 
                            disabled={disabled} 
                            style={{ 
                              backgroundColor: disabled ? '#ccc' : 'red',
                              minWidth: '140px'
                            }}
                          >
                            {disabled ? '🗂️ Archived' : '🗂️ Archive'}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
                  </div>
                );
              })}
            </div>
          );
        })
      )}
      
      {!loading && Object.keys(groupedEvents).length === 0 && (
        <p style={{ textAlign: 'center', color: '#666', marginTop: '40px' }}>
          No events found matching your criteria.
        </p>
      )}
    </div>
  );
}